import axios from 'axios';
import { replaceUrlPlaceholder, replaceUrlPlaceholders, getRequestConfig, REQUEST_TYPES } from '../request';
import API_END_POINTS from '../endpoints';
import { showFileFromBlob } from '../../helpers';

/**
 * Retrieve orders
 * @param {object} data
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiRetrieveOrders = function (data, onSuccess, onError) {
  const config = getRequestConfig(REQUEST_TYPES.GET, API_END_POINTS.ORDER_GET_ALL);
  config.params = data;
  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data, response.headers);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

/**
 * Retrieve orders for suppliers
 * @param {object} data
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiRetrieveOrdersForSuppliers = function (data, onSuccess, onError) {
  const config = getRequestConfig(REQUEST_TYPES.GET, API_END_POINTS.ORDER_GET_ALL_FOR_SUPPLIERS);
  config.params = data;
  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data, response.headers);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

/**
 * Cancel orders
 * @param {object} ids
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiOrdersCancel = function (ids, onSuccess, onError) {
  const config = getRequestConfig(REQUEST_TYPES.POST, API_END_POINTS.ORDER_BULK_CANCEL);
  config.params = {
    orderIds: ids,
  };
  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

/**
 * Mark orders as received
 * @param {object} ids
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiOrdersMarkReceived = function (ids, onSuccess, onError) {
  const config = getRequestConfig(REQUEST_TYPES.POST, API_END_POINTS.ORDER_BULK_RECEIVED);
  config.params = {
    orderIds: ids,
  };
  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

/**
 * Print orders
 * @param {object} ids
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiOrdersPrint = function (ids, onSuccess, onError) {
  const config = getRequestConfig(REQUEST_TYPES.POST, API_END_POINTS.ORDER_BULK_PRINT);
  config.params = {
    orderIds: ids,
  };
  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

/**
 * Retrieve order
 * @param {string} id
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiRetrieveOrder = function (id, onSuccess, onError) {
  const url = replaceUrlPlaceholder(API_END_POINTS.ORDER_GET_ONE, 'or', id);
  const config = getRequestConfig(REQUEST_TYPES.GET, url);
  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

/**
 * Adds line item on order
 * @param {string} orderId
 * @param {string} variantId
 * @param {object} data
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiAddVariantToOrder = function (orderId, variantId, data, onSuccess, onError) {
  const url = replaceUrlPlaceholder(API_END_POINTS.ORDER_ADD_VARIANT, 'or', orderId);
  const config = getRequestConfig(REQUEST_TYPES.POST, url);
  // Set request params
  data = data || {};
  data.variantId = variantId;
  config.data = data;
  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

/**
 * Adds custom line item
 * @param {string} orderId
 * @param {object} data
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiAddCustomLineItemToOrder = function (orderId, data, onSuccess, onError) {
  const url = replaceUrlPlaceholder(API_END_POINTS.ORDER_ADD_CUSTOM_LINE_ITEM, 'or', orderId);
  const config = getRequestConfig(REQUEST_TYPES.POST, url);
  // Set request params
  data = data || {};
  config.data = data;
  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

/**
 * Updates line item of the order
 * @param {string} orderId
 * @param {string} lineItemId
 * @param {object} data
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiUpdateOrderLineItem = function (orderId, lineItemId, data, onSuccess, onError) {
  const url = replaceUrlPlaceholders(API_END_POINTS.ORDER_UPDATE_LINE_ITEM, {
    or: orderId,
    it: lineItemId,
  });
  const config = getRequestConfig(REQUEST_TYPES.PATCH, url);
  // Set request params
  data = data || {};
  config.data = data;
  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

/**
 * Deletes line item from order
 * @param {string} orderId
 * @param {string} lineItemId
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiRemoveLineItemFromOrder = function (orderId, lineItemId, onSuccess, onError) {
  const url = replaceUrlPlaceholders(API_END_POINTS.ORDER_REMOVE_LINE_ITEM, {
    or: orderId,
    it: lineItemId,
  });
  const config = getRequestConfig(REQUEST_TYPES.DEL, url);
  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

/**
 * Add note on order
 * @param {string} orderId
 * @param {string} note
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiOrderAddNote = function (orderId, note, onSuccess, onError) {
  const url = replaceUrlPlaceholder(API_END_POINTS.ORDER_ADD_NOTE, 'or', orderId);
  const config = getRequestConfig(REQUEST_TYPES.PUT, url);
  config.data = { note: note };
  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

/**
 * Cancel order
 * @param {string} orderId
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiOrderCancel = function (orderId, onSuccess, onError) {
  const url = replaceUrlPlaceholder(API_END_POINTS.ORDER_CANCEL, 'or', orderId);
  const config = getRequestConfig(REQUEST_TYPES.POST, url);
  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

export const apiOrderUpdatedSendEmails = function (order, { orderUpdated, invoiceUpdated }, onSuccess, onError) {
  let requests = [];

  if (orderUpdated) {
    const url = replaceUrlPlaceholder(API_END_POINTS.ORDER_RESEND_EMAIL, 'or', order.id);
    const config = getRequestConfig(REQUEST_TYPES.POST, url);
    requests.push(axios.post(config.url, {}, config));
  }

  if (invoiceUpdated) {
    const url = replaceUrlPlaceholder(API_END_POINTS.ORDER_SEND_INVOICE_UPDATED_EMAIL, 'or', order.id);
    const config = getRequestConfig(REQUEST_TYPES.POST, url);
    requests.push(axios.post(config.url, {}, config));
  }

  Promise.all(requests)
    .then((...res) => {
      onSuccess && onSuccess(res);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

/**
 * Adds note for line item
 * @param {string} orderId
 * @param {string} lineItemId
 * @param {string} note
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiOrderLineItemSetNote = function (orderId, lineItemId, note, onSuccess, onError) {
  apiUpdateOrderLineItem(orderId, lineItemId, { note: note }, onSuccess, onError);
};

/**
 * Set line item quantity
 * @param {string} orderId
 * @param {string} lineItemId
 * @param {string} quantity
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiOrderLineItemSetQuantity = function (orderId, lineItemId, quantity, onSuccess, onError) {
  apiUpdateOrderLineItem(orderId, lineItemId, { quantity: quantity }, onSuccess, onError);
};

/**
 * Adds invoice payment method on order
 * @param {string} orderId
 * @param {string} method
 * @param {object} data
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiAddInvoicePaymentMethodOnOrder = function (orderId, method, data, onSuccess, onError) {
  const url = replaceUrlPlaceholders(API_END_POINTS.ORDER_ADD_INVOICE_PAYMENT_METHOD, {
    or: orderId,
  });
  const config = getRequestConfig(REQUEST_TYPES.POST, url);
  // Set request params
  data = data || {};
  data.method = method ? method : null;

  config.data = data;
  axios(config)
    .then((response) => {
      onSuccess(response.data);
    })
    .catch((error) => {
      onError(error);
    });
};

/**
 * Adds payment method on prepurchase draft order
 * @param {string} orderId
 * @param {object} data
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiAddPrepurchasePaymentMethodOnOrder = function (orderId, data, onSuccess, onError) {
  const url = replaceUrlPlaceholders(API_END_POINTS.ORDER_ADD_PREPURCHASE_PAYMENT_METHOD, {
    or: orderId,
  });

  const config = getRequestConfig(REQUEST_TYPES.POST, url);
  // Set request params
  data = data || {};
  config.data = data;

  axios(config)
    .then((response) => {
      onSuccess(response.data);
    })
    .catch((error) => {
      onError(error);
    });
};

/**
 * Adds direct payment method on order
 * @param {string} orderId
 * @param {string} method
 * @param {object} data
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiAddDirectPaymentMethodOnOrder = function (orderId, method, data, onSuccess, onError) {
  const url = replaceUrlPlaceholders(API_END_POINTS.ORDER_ADD_DIRECT_PAYMENT_METHOD, {
    or: orderId,
    md: method,
  });
  const config = getRequestConfig(REQUEST_TYPES.POST, url);
  // Set request params
  data = data || {};
  config.data = data;

  axios(config)
    .then((response) => {
      onSuccess(response.data);
    })
    .catch((error) => {
      onError(error);
    });
};

/**
 * Adds shipping to order
 * @param {string} orderId
 * @param {string} type delivery|pickup
 * @param {object} data
 * @param {string} deliveryCompanyId
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiSetOrderShipping = function (orderId, type, data, deliveryCompanyId, onSuccess, onError) {
  const url = replaceUrlPlaceholders(API_END_POINTS.ORDER_SET_SHIPPING, {
    or: orderId,
    type: type,
  });
  const config = getRequestConfig(REQUEST_TYPES.PUT, url);
  // Set request params
  data = data || {};
  config.data = data;

  axios(config)
    .then((shippingResponse) => {
      if (deliveryCompanyId) {
        // Set draft delivery company
        apiOrderSetDeliveryCompany(
          orderId,
          deliveryCompanyId,
          (deliveryCompanyResponse) => {
            onSuccess && onSuccess(deliveryCompanyResponse);
          },
          (error) => onError && onError(error)
        );
      } else {
        onSuccess && onSuccess(shippingResponse.data);
      }
    })
    .catch((error) => {
      onError && onError(error);
    });
};

/**
 * Reorder order
 * @param {string} orderId
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiReorderOrder = function (orderId, onSuccess, onError) {
  const url = replaceUrlPlaceholder(API_END_POINTS.ORDER_REORDER, 'or', orderId);
  const config = getRequestConfig(REQUEST_TYPES.POST, url);
  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

/**
 * Refund for order
 * @param {string} orderId
 * @param {object} data
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiRefundOrder = function (orderId, data, onSuccess, onError) {
  const url = replaceUrlPlaceholders(API_END_POINTS.ORDER_REFUND, {
    or: orderId,
  });

  const config = getRequestConfig(REQUEST_TYPES.POST, url);
  // Set request params
  data = data || {};
  config.data = data;

  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

/**
 * Book order
 * @param {string} orderId
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiOrderBook = function (orderId, onSuccess, onError) {
  const url = replaceUrlPlaceholder(API_END_POINTS.ORDER_BOOK, 'or', orderId);
  const config = getRequestConfig(REQUEST_TYPES.POST, url);
  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

/**
 * Order book payment
 * @param {string} orderId
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiOrderBookPayment = function (orderId, onSuccess, onError) {
  const url = replaceUrlPlaceholder(API_END_POINTS.ORDER_BOOK_PAYMENT, 'or', orderId);
  const config = getRequestConfig(REQUEST_TYPES.POST, url);
  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

/**
 * Adds order shipping as delivery
 * @param {string} orderId
 * @param {object} data
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiSetOrderShippingAsDelivery = function (orderId, data, onSuccess, onError) {
  apiSetOrderShipping(orderId, 'delivery', data, onSuccess, onError);
};

/**
 * Adds order shipping as pickup
 * @param {string} orderId
 * @param {object} data
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiSetOrderShippingAsPickup = function (orderId, data, onSuccess, onError) {
  apiSetOrderShipping(orderId, 'pickup', data, onSuccess, onError);
};

/**
 * Set delivery company
 * @param {string} orderId
 * @param {string} companyId
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiOrderSetDeliveryCompany = function (orderId, companyId, onSuccess, onError) {
  const url = replaceUrlPlaceholder(API_END_POINTS.ORDER_PUT_DELIVERY_COMPANY, 'or', orderId);
  const config = getRequestConfig(REQUEST_TYPES.PUT, url);
  config.data = { id: companyId };
  axios(config)
    .then((response) => {
      onSuccess(response.data);
    })
    .catch((error) => {
      onError(error);
    });
};

/**
 * Delete delivery company
 * @param {string} orderId
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiOrderDeleteDeliveryCompany = function (orderId, onSuccess, onError) {
  const url = replaceUrlPlaceholder(API_END_POINTS.ORDER_DELETE_DELIVERY_COMPANY, 'or', orderId);
  const config = getRequestConfig(REQUEST_TYPES.DEL, url);
  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

/**
 * Add existing customer to order
 * @param {string} orderId
 * @param {object} data
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiAddCustomerOnOrder = function (orderId, data, onSuccess, onError) {
  const url = replaceUrlPlaceholder(API_END_POINTS.ORDER_ADD_CUSTOMER, 'or', orderId);
  const config = getRequestConfig(REQUEST_TYPES.PUT, url);

  // Set request params
  data = data || {};
  config.data = data;

  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

/**
 * Remove customer from order
 * @param {string} orderId
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiRemoveCustomerFromOrder = function (orderId, onSuccess, onError) {
  const url = replaceUrlPlaceholder(API_END_POINTS.ORDER_REMOVE_CUSTOMER, 'or', orderId);
  const config = getRequestConfig(REQUEST_TYPES.DEL, url);
  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

export const apiOrderAddDirectDiscount = (orderId, data, onSuccess, onError) => {
  const url = replaceUrlPlaceholder(API_END_POINTS.ORDER_ADD_DIRECT_DISCOUNT, 'or', orderId);
  const config = getRequestConfig(REQUEST_TYPES.POST, url);
  config.data = data;
  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

export const apiOrderDeleteDiscount = (orderId, onSuccess, onError) => {
  const url = replaceUrlPlaceholder(API_END_POINTS.ORDER_DELETE_DISCOUNT, 'or', orderId);
  const config = getRequestConfig(REQUEST_TYPES.DEL, url);
  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

export const apiOrderPackingListBulkPrint = (orderIds, onSuccess, onError) => {
  const config = getRequestConfig(REQUEST_TYPES.POST, API_END_POINTS.ORDER_PACKING_LIST_BULK_PRINT);
  config.data = { orderIds: orderIds };
  config.responseType = 'blob';

  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

/**
 * Approve order
 * @param {string} orderId
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiApproveOrder = function (orderId, onSuccess, onError) {
  const url = replaceUrlPlaceholder(API_END_POINTS.ORDER_APPROVE, 'or', orderId);
  const config = getRequestConfig(REQUEST_TYPES.PUT, url);
  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

/**
 * Approve order line items
 * @param {string} orderId
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiApproveOrderLineItems = function (orderId, onSuccess, onError) {
  const url = replaceUrlPlaceholder(API_END_POINTS.ORDER_MARK_APPROVED, 'or', orderId);
  const config = getRequestConfig(REQUEST_TYPES.POST, url);
  axios(config)
    .then((response) => {
      onSuccess && onSuccess(response.data);
    })
    .catch((error) => {
      onError && onError(error);
    });
};

/**
 * Get order invoice
 * @param {string} hash
 * @param {function} onSuccess
 * @param {function} onError
 */
export const apiGetOrderInvoice = function (hash, onSuccess, onError) {
  const url = replaceUrlPlaceholder(API_END_POINTS.ORDER_GET_INVOICE, 'hash', hash);
  const config = getRequestConfig(REQUEST_TYPES.GET, url);
  config.responseType = 'arraybuffer';
  config.headers['Accept'] = 'application/pdf';

  axios(config)
    .then((response) => {
      const pdfUrl = showFileFromBlob(response.data, 'invoice');
      onSuccess && onSuccess(pdfUrl);
    })
    .catch((error) => {
      onError && onError(error);
    });
};
